import React from 'react';
import uniqBy from 'lodash/uniqBy';
import SelectOptionsIcon from 'common/SelectOptionsIcon';

/**
 * @param {Array} products The raw fertilizer products fetched from the backend
 * @returns {Array} The hybrid dropdown options
 */
export const formatSelectOptionsFromFertilizerProducts = (products) => {
  return products.map(({ name, id, matterState, isCustom, company }) => ({
    id,
    value: `${name}`,
    matterState,
    isCustom,
    description: company
  }));
};

/**
 * @param {Array} products The raw products fetched from the backend
 * @param {boolean} canDisplayIcon The flag to determine if icon needs to be displayed
 * @returns {Array} The hybrid dropdown options
 */
export const createProductOptions = (products, canDisplayIcon = false) => {
  const uniqueProducts = uniqBy(products, 'id');
  return uniqueProducts.map(entry => {
    return {
      createdBy: entry.info && entry.info.source ? entry.info.source.createdBy : null,
      brand: entry.brand,
      description: entry.brand,
      icon: canDisplayIcon 
        ? <SelectOptionsIcon 
          backupNumber={null} 
          isPrimary={entry.primary}
          tested={!entry.generic} />
        : null,
      id: entry.id,
      name: entry.name,
      primary: entry.primary,
      score: entry.score,
      value: entry.name
    };
  });
};

/**
 * @param {Array} backupList Backup hybrid list
 * @returns {Map} Key: dealerId, Value: all backup hybrids that belong to the dealer
 */
export const groupBackupHybridsByDealer = (backupList) => {
  let dealerToBackupHybridMap = new Map();
  backupList.forEach((entry) => {
    const key = entry.createdBy;
    if (dealerToBackupHybridMap.has(key)) {
      let hybridsFromSameGroup = dealerToBackupHybridMap.get(key);
      hybridsFromSameGroup.push(entry);
      dealerToBackupHybridMap.set(key, hybridsFromSameGroup);
    } else {
      dealerToBackupHybridMap.set(key, [entry]);
    }
  });
  return dealerToBackupHybridMap;
};

/**
 * @param {Map} dealerToBackupHybridMap Key: dealerId, Value: all backup hybrids that belong to the dealer
 * @returns {Map} Key: dealerId, Value: all sorted backup hybrids that belong to the dealer
 */
export const sortDealerBucketsByScoreAndName = (dealerToBackupHybridMap) => {
  const sortedDealerToBackupHybridMap = new Map();
  dealerToBackupHybridMap.forEach((backups, dealer) => {
    const sortedBackups = backups.sort((a, b) => b.score - a.score || a.value.localeCompare(b.value));
    sortedDealerToBackupHybridMap.set(dealer, sortedBackups);
  });
  return sortedDealerToBackupHybridMap;
};

/**
 * @param {Map} sortedDealerToBackupHybridMap Key: dealerId, Value: all sorted backup hybrids that belong to the dealer
 * @returns {Map} Key: dealerId, Value: all sorted backup hybrids that belong to the dealer with valid backup number
 */
export const assignBackupNumber = (sortedDealerToBackupHybridMap) => {
  let dealerToBackupNumberHybridMap = new Map();
  sortedDealerToBackupHybridMap.forEach((sortedBackups, dealer) => {
    const sortedBackupsWithNumber = sortedBackups.map((option, index) => {
      return {
        ...option,
        backupNumber: index + 1,
        icon: (
          <SelectOptionsIcon 
            backupNumber={index + 1}
            isPrimary={false}
            score={option.score}
            tested={!option.generic} 
          />)
      };
    });
    dealerToBackupNumberHybridMap.set(dealer, sortedBackupsWithNumber);
  });
  return dealerToBackupNumberHybridMap;
};

/**
 * @param {Map} dealerToBackupNumberHybridMap Key: dealerId, Value: all sorted and numberd backups of the dealer
 * @returns {Array} 
 */
export const sortBackupHybrids = (dealerToBackupNumberHybridMap) => {
  let backupHybridWithNumberList = [];
  dealerToBackupNumberHybridMap.forEach((backups) => {
    backupHybridWithNumberList = backupHybridWithNumberList.concat(backups);
  });

  backupHybridWithNumberList.sort((a, b) => 
    (a.backupNumber === null) - (b.backupNumber === null)
    || a.backupNumber - b.backupNumber
    || a.value.localeCompare(b.value));

  return backupHybridWithNumberList;
};

/**
 * @param {Array} options All recommended hybrids options
 * @returns {Array} Primary hybrid options
 */
export const createPrimaryList = (options) => {
  return options.filter(o => o.primary).map((entry) => {
    return {
      ...entry,
      backupNumber: null,
      icon: (
        <SelectOptionsIcon 
          backupNumber={null}
          isPrimary={true}
          score={entry.score}
          tested={!entry.generic} 
        />
      )
    };
  });
};

/**
 * @param {Array} options All recommended hybrids options
 * @returns {Array} Sorted-by-backup-number backup hybrid options
 */
export const createSortedBackupList = (options) => {
  const originalBackupList = options.filter(o => !o.primary && o.score >= 0);
  const dealerToBackupHybridMap = groupBackupHybridsByDealer(originalBackupList);
  const sortedDealerToBackupHybridMap = sortDealerBucketsByScoreAndName(dealerToBackupHybridMap);
  const dealerToBackupNumberHybridMap = assignBackupNumber(sortedDealerToBackupHybridMap);
  return sortBackupHybrids(dealerToBackupNumberHybridMap);
};

/**
 * @param {Array} options All recommended hybrids options
 * @returns {Array} Do-not-plant hybrid options
 */
export const createDoNotPlantList = (options) => {
  const doNotPlantList = options.filter(o => !o.primary && o.score < 0);
  return doNotPlantList.map((entry) => {
    return {
      ...entry,
      backupNumber: null,
      icon: (
        <SelectOptionsIcon 
          backupNumber={null}
          isPrimary={false}
          score={entry.score}
          tested={!entry.generic} 
        />
      )
    };
  });
};
